/**
 * Service: MFA (Multi-Factor Authentication)
 * Wraps Supabase TOTP MFA operations.
 */
import { supabase } from "@/integrations/supabase/client";

/** Enroll a new TOTP factor. Returns the factor id, QR code, secret, etc. */
export async function enrollTOTP(friendlyName = "Authenticator App") {
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: "totp",
    friendlyName,
  });
  return { data, error: error?.message ?? null };
}

/** Verify a TOTP code to complete enrollment of a factor. */
export async function verifyTOTP(factorId: string, code: string) {
  const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
  if (challengeError) return { data: null, error: challengeError.message };

  const { data, error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.id,
    code,
  });
  return { data, error: error?.message ?? null };
}

/** List all enrolled factors for the current user. */
export async function listFactors() {
  const { data, error } = await supabase.auth.mfa.listFactors();
  return { data, error: error?.message ?? null };
}

/** Unenroll (remove) a factor by id. */
export async function unenrollFactor(factorId: string) {
  const { data, error } = await supabase.auth.mfa.unenroll({ factorId });
  return { data, error: error?.message ?? null };
}

/** Get the current MFA assurance level. */
export async function getAssuranceLevel() {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  return { data, error: error?.message ?? null };
}
